import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Channel, ConfirmationComponent, Department, DepartmentService, Pager, SettingsAttendance, SettingsAttendanceService, TeamService } from 'lib-trend-core';
import { catchError, mergeMap, of, takeUntil } from 'rxjs';
import { DepartmentEditComponent } from './department-edit/department-edit.component';
import { DepartmentScheduleComponent } from './department-schedule/department-schedule.component';

interface DepartmentHeadCount {
  _id: string;
  agentCount: number;
  supervisorCount: number;
}

@Component({
    selector: 'department-component',
    // templateUrl: 'department.component.html'
    templateUrl: 'department.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DepartmentComponent extends AbstractComponent implements OnInit {

  pager: Pager<Department> = new Pager<Department>({ perPage: 10 });

  @Input() channel!: Channel;

  private totalAttendants!: Array<DepartmentHeadCount>;

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    public departmentService: DepartmentService,
    public teamService: TeamService,
    private settingsAttendanceService: SettingsAttendanceService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getList();
  }

  private getList() {
    if (this.isAdmin()) {
      this.searchParams = {
        company: this.getIDCurrentCompany(),
        channel: this.channel._id
      };
      this.departmentService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
        .pipe(
          mergeMap((pager: Pager<Department>) => {
            this.pager = pager;

            const ids = this.pager.list.map((department: Department) => department._id);
            return this.departmentService.getCountAttendantsByDepartment(ids).pipe(catchError(() => of([])));
          }),
        ).subscribe((counts: any) => {
          this.totalAttendants = counts;
        });
    } else {
      this.searchParams = {
        company: this.getIDCurrentCompany(),
        user: this.getIDCurrentUser(),
        channel: this.channel._id
      };
      this.teamService.getDepartmentFromSupervisor(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
        .pipe(
          mergeMap((pager: Pager<Department>) => {
            this.pager = pager;

            const ids = this.pager.list.map((department: Department) => department._id);
            return this.departmentService.getCountAttendantsByDepartment(ids).pipe(catchError(() => of([])));
          }),
        ).subscribe((counts: any) => {
          this.totalAttendants = counts;
        });
    }
  }

  getCountAttendants(departmentId: string): string {
    if (!this.totalAttendants) return this.t('department.list.noAttendantsSupervisor');

    const department = this.totalAttendants.find((dept: DepartmentHeadCount) => dept._id === departmentId);

    const totalAgents = department?.agentCount ?? 0;
    const totalSupervisors = department?.supervisorCount ?? 0;

    const agentText = totalAgents === 0
      ? this.t('department.list.noAgent')
      : totalAgents > 1
        ? this.t('department.list.agentsCount', { count: totalAgents })
        : this.t('department.list.agentSingular', { count: totalAgents });
    const supervisorText = totalSupervisors === 0
      ? this.t('department.list.noSupervisor')
      : totalSupervisors > 1
        ? this.t('department.list.supervisorsCount', { count: totalSupervisors })
        : this.t('department.list.supervisorSingular', { count: totalSupervisors });

    return `${agentText}\n${supervisorText}`;
  }

  addDepartment() {
    const department = {} as Department;
    department.channel = {} as Channel;
    department.channel._id = this.channel._id;
    const dialogRef = this.dialog.open(DepartmentEditComponent, {
      width: '700px',
      height: '700px',
      data: department
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.getList();
      }
    });
  }

  edit(item: Department): void {
    const department = {} as Department;
    department._id = item._id;
    department.channel = {} as Channel;
    department.channel._id = this.channel._id;
    const dialogRef = this.dialog.open(DepartmentEditComponent, {
      width: '700px',
      height: '700px',
      data: department
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.getList();
      }
    });
  }

  delete(item: Department): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) !== true) {
        return;
      }

      // Deleting the channel's default department leaves the channel without a target for
      // inbound attendances — ask a second time, naming the consequence, before going ahead.
      this.settingsAttendanceService.getByChannel(this.channel._id)
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((settings: SettingsAttendance | null) => {
          const isDefaultDepartment = this.isDefaultDepartment(settings, item);
          if (!isDefaultDepartment) {
            this.performDelete(item, false);
            return;
          }

          const warningRef = this.dialog.open(ConfirmationComponent, {
            width: '600px',
            data: this.t('department.list.confirmDeleteDefault', { name: item.name }),
          });
          warningRef.afterClosed().subscribe(confirmed => {
            if (Boolean(confirmed) === true) {
              this.performDelete(item, true);
            }
          });
        });
    });
  }

  private performDelete(item: Department, wasDefaultDepartment: boolean): void {
    this.departmentService.delete(item._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // The API unlinks the department from the channel settings — tell the user a new
          // default has to be picked so inbound attendances keep being routed.
          this.alertService.success(this.t(wasDefaultDepartment ? 'department.list.deleteDefaultSuccess' : 'department.list.deleteSuccess'));
          this.getList();
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  /** `departmentDefault` comes unpopulated (id string) from the API, but tolerate a populated object too. */
  private isDefaultDepartment(settings: SettingsAttendance | null, item: Department): boolean {
    const departmentDefault = settings?.departmentDefault as Department | string | null | undefined;
    if (!departmentDefault) {
      return false;
    }
    const idDefault = typeof departmentDefault === 'string' ? departmentDefault : departmentDefault._id;
    return String(idDefault) === String(item._id);
  }

  schedule(item: Department): void {
    const department = {} as Department;
    department._id = item._id;
    department.channel = {} as Channel;
    department.channel._id = this.channel._id;
    const dialogRef = this.dialog.open(DepartmentScheduleComponent, {
      width: '1200px',
      height: '745px',
      data: department
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.getList();
      }
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

}
